/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { AxisOptions, Options } from 'highcharts';
import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import datasetTypes from '../../enums/datasetTypes';
import GenericChartVisualization from './chart/GenericChartVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import visitMultiAxisDatasetNode from '../../store/util/visitMultiAxisDatasetNode';
import {
    getMeasures,
    getTooltipValue,
    heatMapRowsToArrayVisitor
} from './chart/util/GenericSeriesUtil';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from './chart/util/EventUtil';
import i18n from '../../../i18n/AdHocBundle.properties';

const colorAxisDefaultPalette = {
    stops: [
        [0.1, '#F6365F'],
        [0.5, '#FFD961'],
        [0.9, '#50C463']
    ]
};

export class HeatMapVisualization extends GenericChartVisualization {

    useShadow = false

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'heatmap',
            backgroundColor: '#FFFFFF',
            plotBackgroundColor: 'rgba(0,0,0,0)',
            renderTo: 'chartContainer',
            zoomType: 'xy',
            polar: false,
            style: {
                fontSize: '1em'
            }
        };
    }

    getLegendSection() {
        const section = super.getLegendSection();

        delete section.symbolWidth;
        delete section.symbolHeight;
        section.padding = 15;

        return section;
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): any {
        const dataset = this.getDataset();
        const linkOptions :ILinkOptions = this.getLinkOptions();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        let firstMinMaxRecorded :boolean = false;
        let min: number = 0,
            max: number = 0;
        const data: any[] = [];

        const columnSeries = this.getColumnSeries();

        const rowsArray = visitMultiAxisDatasetNode(dataset.data.rows, heatMapRowsToArrayVisitor, {
            array: [],
            indexes: [],
            selectedRowIndex
        }, true);

        columnSeries.forEach((categoryColumn: any, columnIndex: number) => {
            rowsArray.array.forEach((categoryRow: any, rowIndex: number) => {
                const value = dataset.data.aggregations[categoryRow.index][categoryColumn.colIndex]
                if (value !== null) {
                    if (firstMinMaxRecorded) {
                        min = value;
                        max = value;
                        firstMinMaxRecorded = true;
                    } else {
                        const numericValue = parseFloat(value);
                        min = Math.min(numericValue, min);
                        max = Math.max(numericValue, max);
                    }
                }

                const linkOutputParams: any = {
                    // the heatmap has only one measure
                    measures: [getOutputParamFromMeasure(measures[0])]
                };
                const usedRowParams = dataset.summary.rows.items.slice(0, selectedRowIndex);
                const usedColParams = dataset.summary.columns.items.slice(0, selectedColIndex);
                const rowParamsValues: any[] = usedRowParams.map((item: any, parameterIndex: number) => {
                    const result: any = {
                        key: getOutputParamsKeyFromField(item),
                        value: categoryRow.label
                    }
                    return result;
                });
                const colParamsValues = usedColParams.map((item: any, parameterIndex: number) => {
                    const result: any = {
                        key: getOutputParamsKeyFromField(item),
                        value: categoryColumn.series.path[selectedColIndex],
                    };
                    return result;
                });
                linkOutputParams.values = rowParamsValues.concat(colParamsValues);

                const serie = {
                    x: columnIndex,
                    y: rowIndex,
                    value: value === null ? value : parseFloat(value),
                    linkOutputParams
                }
                data.push(serie);
            });
        });

        this.recordMinMaxValues(min, max);

        return [
            {
                borderWidth: 1,
                data,
                point: {
                    events: getEvents(linkOptions)
                }
            }
        ];
    }

    finalOptionAdjustment(options: Options): Options {
        const currentOptions = options;
        const dataset = this.getDataset();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const selectedRowIndex = this.getSelectedRowIndex();

        const columnSeries = this.getColumnSeries();

        const rowsArray = visitMultiAxisDatasetNode(dataset.data.rows, heatMapRowsToArrayVisitor, {
            array: [],
            indexes: [],
            selectedRowIndex
        }, true);

        let xAxisCategories: string[];
        let isColumnTotal = false;
        if (columnSeries.length === 1 && columnSeries[0].series.path.length === 1) {
            xAxisCategories = [i18n['adhoc.node.total.node']];
            isColumnTotal = true;
        } else {
            xAxisCategories = columnSeries.map((column) => {
                let item :any = column.series.path[0];
                if (item === null) {
                    item = '';
                }
                return item;
            })
        }
        const xAxis: AxisOptions = {
            categories: xAxisCategories,
            title: {
                text: ''
            },
            labels: {
                step: 1,
                maxStaggerLines: 1,
                style: {
                    fontSize: '1em'
                },
                rotation: -45
            },
            offset: 0,
            gridLineWidth: 0,
        };
        if (!isColumnTotal) {
            xAxis.minRange = 1;
        }

        let yAxisCategories: any[];
        let isRowTotal = false;
        if (rowsArray.array.length === 1 && rowsArray.array[0].children.length === 0) {
            yAxisCategories = [i18n['adhoc.node.total.node']];
            isRowTotal = true;
        } else {
            yAxisCategories = rowsArray.array.map((row) => {
                return row.label;
            })
        }

        const yAxis: AxisOptions = {
            title: null,
            labels: {
                rotation: 0,
                step: 1,
                style: {
                    fontSize: '1em'
                }
            },
            gridLineWidth: 0,
            categories: yAxisCategories,
        };
        if (!isRowTotal) {
            yAxis.minRange = 1;
        }

        const getTooltipProps = {
            // there is only one measure always, so we don't have to worry about finding the right measure id
            measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        const newOptions = {
            ...currentOptions,
            tooltip: {
                valueDecimals: 2,
                useHTML: true,
                style: {
                    fontSize: '1em'
                },
                formatter() {
                    const measurePrefix = '<span style="color:{point.color}">\u25CF</span>';
                    const chartPoint: any = this;
                    const pointX = chartPoint.series.xAxis.categories[chartPoint.point.x];
                    const pointY = chartPoint.series.yAxis.categories[chartPoint.point.y];
                    const tooltipValue = getTooltipValue(getTooltipProps, chartPoint.point.value);
                    const measureLabel = measures[0].label;
                    return `${pointX}, ${pointY}<br/>${measurePrefix} ${measureLabel}: <b>${tooltipValue}</b>`;
                },
            },
            xAxis,
            yAxis,
            colorAxis: {
                ...colorAxisDefaultPalette,
                labels: {
                    style: {
                        fontSize: '1em'
                    }
                },
                tickPixelInterval: 150,
                min: this.getRecordedMinMaxValues().min,
                max: this.getRecordedMinMaxValues().max
            }
        };
        return super.finalOptionAdjustment(newOptions);
    }
}

export const HeatMapVisualizationDescriptor = {
    legacyAdhoc: 'heat_map',
    parentClass: 'Range',
    cssClass: 'HeatMap',
    bundleName: 'adhoc.visualisation.chooser.type.HEAT_MAP',
    name: 'HeatMap',
    group: 'adhoc.visualisation.chooser.type.GROUP_RANGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: HeatMapVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 1,
            max: 1,
            inRow: false
        },
        fields: {
            min: 2,
            max: 2
        },
        placement: {
            allowed: [{
                column: 'fm',
                row: 'f'
            }]
        }
    }
};
